import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, ShieldCheck, PenLine, ArrowLeft } from "lucide-react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";

/**
 * Introducer contract signing page.
 *
 * Two states:
 *   - not signed  → shows the PDF inline (iframe) + acknowledgement checkbox + "Firma" button
 *   - signed      → shows the countersigned PDF inline + evidence panel (timestamp, hash)
 */
export default function IntroducerContractPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [ack, setAck] = useState(false);
  const [signing, setSigning] = useState(false);
  const BACKEND_URL =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : (process.env.REACT_APP_BACKEND_URL || "");

  useEffect(() => {
    api
      .get("/introducer/me")
      .then(({ data }) => setMe(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  // Signed PDF viewer URL — cache-bust each time contract state (me) changes
  // so freshly-signed PDF replaces the pre-signature preview.
  const pdfUrl = useMemo(
    () => (BACKEND_URL ? `${BACKEND_URL}/api/introducer/contract#t=${Date.now()}` : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [BACKEND_URL, me?.contract_signed]
  );

  const sign = async () => {
    setError("");
    setSigning(true);
    try {
      await api.post("/introducer/contract/sign", {});
      const { data } = await api.get("/introducer/me");
      setMe(data);
      setAck(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSigning(false);
    }
  };

  return (
    <PortalLayout>
      <section
        className="max-w-[1080px] mx-auto px-6 sm:px-8 pt-12 pb-12"
        data-testid="introducer-contract-page"
      >
        <button
          onClick={() => navigate("/introducer")}
          className="inline-flex items-center gap-2 br-overline mb-6 hover:text-[color:var(--br-parchment)] transition-colors"
          data-testid="contract-back-btn"
        >
          <ArrowLeft size={14} /> {t("intro.contract_back")}
        </button>

        <div className="br-fade-up">
          <div className="br-overline">{t("intro.contract_overline")}</div>
          <h1 className="font-serif-display text-[clamp(2rem,3.8vw,3.2rem)] mt-3">
            {t("intro.contract_heading_a")}{" "}
            <em>{t("intro.contract_heading_em")}</em>
          </h1>
          <p className="text-[1rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
            {t("intro.contract_intro")}
          </p>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="contract-error">
            {error}
          </div>
        )}

        {me && (
          <>
            {/* Status pill */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {me.contract_signed ? (
                <span
                  className="inline-flex items-center gap-2 font-mono-fin text-[0.7rem] tracking-[0.18em] uppercase px-3 py-2"
                  style={{ color: "var(--br-bronze-soft)", border: "1px solid var(--br-bronze-soft)" }}
                  data-testid="contract-status-signed"
                >
                  <ShieldCheck size={14} /> {t("intro.contract_status_signed")}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-2 font-mono-fin text-[0.7rem] tracking-[0.18em] uppercase px-3 py-2"
                  style={{ color: "var(--br-red)", border: "1px solid var(--br-red)" }}
                  data-testid="contract-status-pending"
                >
                  <PenLine size={14} /> {t("intro.contract_status_pending")}
                </span>
              )}
              {me.contract_signed && me.contract_signed_at && (
                <span className="font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">
                  {t("intro.contract_signed_on")}: {me.contract_signed_at}
                </span>
              )}
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex items-center gap-2 br-btn-outline"
                style={{ padding: "8px 16px", fontSize: "0.72rem" }}
                data-testid="contract-download-btn"
              >
                <Download size={14} /> {t("intro.contract_download")}
              </a>
            </div>

            {/* PDF preview */}
            <div
              className="mt-6 br-card overflow-hidden"
              style={{ padding: 0, background: "#0e1422" }}
              data-testid="contract-viewer"
            >
              <iframe
                src={pdfUrl}
                title="Contract PDF"
                className="w-full"
                style={{ height: "80vh", border: 0, background: "#3B3327" }}
              />
            </div>

            {/* Signature action (only if not signed) */}
            {!me.contract_signed && (
              <div
                className="mt-8 br-card br-aurora-ribbon px-8 py-8"
                data-testid="contract-sign-panel"
              >
                <div className="br-overline">{t("intro.contract_sign_overline")}</div>
                <h2 className="font-serif-display text-[1.8rem] mt-3">
                  {t("intro.contract_sign_heading")}
                </h2>
                <p className="text-[0.95rem] mt-3 text-[color:var(--br-text)] leading-relaxed max-w-[720px]">
                  {t("intro.contract_sign_body")}
                </p>
                <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-[color:var(--br-bronze)] h-4 w-4"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    data-testid="contract-ack-checkbox"
                  />
                  <span className="text-[0.9rem] text-[color:var(--br-text)] leading-relaxed">
                    {t("intro.contract_ack_label")}
                  </span>
                </label>
                <div className="mt-6 flex flex-wrap gap-3 items-center">
                  <button
                    type="button"
                    onClick={sign}
                    disabled={!ack || signing}
                    className="br-btn-primary inline-flex items-center gap-2"
                    data-testid="contract-sign-btn"
                  >
                    <PenLine size={16} />
                    {signing ? t("intro.contract_signing") : t("intro.contract_sign_cta")}
                  </button>
                  <span className="font-mono-fin text-[0.7rem] tracking-[0.18em] uppercase text-[color:var(--br-text-dim)]">
                    {t("intro.contract_legal_note")}
                  </span>
                </div>
              </div>
            )}

            {me.contract_signed && (
              <div
                className="mt-8 br-card px-8 py-8"
                data-testid="contract-signed-panel"
              >
                <div className="br-overline">{t("intro.contract_signed_overline")}</div>
                <h2 className="font-serif-display text-[1.6rem] mt-3">
                  {t("intro.contract_signed_heading")}
                </h2>
                <p className="text-[0.9rem] mt-3 text-[color:var(--br-text-dim)] leading-relaxed max-w-[720px]">
                  {t("intro.contract_signed_body")}
                </p>
                <Link
                  to="/introducer"
                  className="br-btn-outline inline-block mt-6"
                  data-testid="contract-back-dashboard"
                >
                  {t("intro.contract_back_dashboard")}
                </Link>
              </div>
            )}
          </>
        )}
      </section>
    </PortalLayout>
  );
}
